import React from "react";
import { Text, Transformer,Image as KonvaImage, } from "react-konva";


export const Rectangle = ({
  shapeProps = {},
  onSelect,
  onChange,
  setBaseImageUrl,
  isSelected,
  showDeleteButton = true, // New prop to control delete button visibility
}) => {
  const shapeRef = React.useRef();
  const transformerRef = React.useRef();
  const [image, setImage] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const baseImageUrl = shapeProps.src || "";

  if (setBaseImageUrl && baseImageUrl) {
    setBaseImageUrl(baseImageUrl);
  }

  // Load the image dynamically
  React.useEffect(() => {
    if (baseImageUrl) {
      setIsLoading(true);
      const img = new window.Image();
      img.crossOrigin = "Anonymous";
      img.src = baseImageUrl;
      img.onload = () => {
        setImage(img);
        setIsLoading(false);
      };
      img.onerror = () => {
        console.error("Failed to load image:", baseImageUrl);
        setIsLoading(false);
      };
    } else {
      setIsLoading(false);
    }
  }, [baseImageUrl]);

  // Update transformer when selection changes
  React.useEffect(() => {
    if (transformerRef.current) {
      if (isSelected && shapeProps.type === "art2") {
        transformerRef.current.nodes([shapeRef.current]);
        transformerRef.current.getLayer()?.batchDraw();
      } else {
        transformerRef.current.nodes([]);
      }
    }
  }, [isSelected, shapeProps.type]);

  // Handle image deletion
  const handleDelete = (event) => {
    event.evt.stopPropagation();
    onChange({ ...shapeProps, delete: true });
  };

  // Handle drag end for Art2
  const handleDragEnd = (e) => {
    if (shapeProps.type === "art2") {
      const node = e.target;
      onChange({
        ...shapeProps,
        x: node.x(),
        y: node.y(),
      });
    }
  };

  // Handle transform end for Art2
  const handleTransformEnd = (e) => {
    if (shapeProps.type === "art2") {
      const node = e.target;
      const scaleX = node.scaleX();
      const scaleY = node.scaleY();

      // Reset scale and update width/height using uniform scale to preserve aspect ratio
      node.scaleX(1);
      node.scaleY(1);

      const uniformScale = Math.min(scaleX, scaleY);
      onChange({
        ...shapeProps,
        x: node.x(),
        y: node.y(),
        width: Math.max(50, (shapeProps.width || 100) * uniformScale),
        height: Math.max(50, (shapeProps.height || 100) * uniformScale),
      });
    }
  };

  const crossX = (shapeProps.x || 0) + (shapeProps.width || 100) - 25;
  const crossY = (shapeProps.y || 0) + 5;

  // Don't render anything while loading and no image
  if (isLoading && !image) {
    return null;
  }

  // Don't render if no image URL and no loaded image
  if (!image && !baseImageUrl) {
    return null;
  }

  return (
    <React.Fragment>
      {/* Render KonvaImage only when image is available */}
      {image && (
        <KonvaImage
          onClick={onSelect}
          onTap={onSelect}
          ref={shapeRef}
          {...shapeProps}
          image={image}
          draggable={shapeProps.type === "art2"}
          onDragEnd={handleDragEnd}
          onTransformEnd={handleTransformEnd}
        />
      )}

      {/* Transformer for Art2 only - show when selected and image is loaded */}
      {shapeProps.type === "art2" && isSelected && image && !isLoading && (
        <Transformer
          ref={transformerRef}
          keepRatio={true}
          enabledAnchors={[
            "top-left",
            "top-right",
            "bottom-left",
            "bottom-right",
          ]}
          boundBoxFunc={(oldBox, newBox) => {
            if (newBox.width < 50 || newBox.height < 50) {
              return oldBox;
            }
            return newBox;
          }}
        />
      )}

      {/* Delete icon - only show when showDeleteButton is true and image is fully loaded */}
      {showDeleteButton && image && !isLoading && (
        <Text
          text="✖"
          fontSize={20}
          fill="red"
          x={crossX}
          y={crossY}
          onClick={handleDelete}
          onTap={handleDelete}
          draggable={false}
        />
      )}
    </React.Fragment>
  );
};